// UserProfileContext.js — single onSnapshot on users/{uid} for all user-doc reads.

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { firestore } from "../services/firebaseConfig";
import { useAuth } from "./AuthContext";

const UserProfileContext = createContext();

const DEFAULT_WORKSPACES = [
  { id: "work", name: "Work", position: 0 },
  { id: "personal", name: "Personal", position: 1 },
];

export const UserProfileProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [hasSeenSignupPrompt, setHasSeenSignupPrompt] = useState(false);
  const [signupPromptDismissed, setSignupPromptDismissed] = useState(false);
  const [signupBannerDismissed, setSignupBannerDismissed] = useState(false);
  const [preferredPlan, setPreferredPlan] = useState("annual");
  const [layoutPreference, setLayoutPreferenceState] = useState("normal");
  const [accountType, setAccountType] = useState(null);
  const [platformFirstSeen, setPlatformFirstSeen] = useState(null);
  const [profileReady, setProfileReady] = useState(false);
  const seedingRef = useRef(false);

  useEffect(() => {
    if (!uid) {
      setWorkspaces([]);
      setWorkspacesLoading(false);
      setHasSeenSignupPrompt(false);
      setSignupPromptDismissed(false);
      setSignupBannerDismissed(false);
      setPreferredPlan("annual");
      setLayoutPreferenceState("normal");
      setAccountType(null);
      setPlatformFirstSeen(null);
      setProfileReady(false);
      seedingRef.current = false;
      return;
    }

    const userRef = doc(firestore, "users", uid);

    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) return; // Wait for ensureUserDoc to create it

        const data = snap.data();
        const fromCache = !!snap.metadata?.fromCache;

        // Use saved workspaces if present (non-empty array). Only seed when the field is
        // missing (new user); never overwrite when workspaces is [] so we don't wipe
        // custom workspaces if something ever wrote an empty array.
        // Only seed from SERVER snapshot — if we seed on a cached snapshot (e.g. iOS stale cache),
        // we would overwrite the server's real workspace list with defaults.
        if (data.workspaces && Array.isArray(data.workspaces) && data.workspaces.length > 0) {
          const sorted = [...data.workspaces].sort(
            (a, b) => (a.position ?? 0) - (b.position ?? 0)
          );
          setWorkspaces(sorted);
        } else if (data.workspaces === undefined && !seedingRef.current) {
          if (fromCache) {
            // Stale cache may show undefined while server has custom workspaces. Show defaults
            // locally only; do NOT write. When server snapshot arrives we'll seed if still undefined.
            setWorkspaces(DEFAULT_WORKSPACES);
          } else {
            seedingRef.current = true;
            updateDoc(userRef, {
              workspaces: DEFAULT_WORKSPACES,
              workspacesVersion: 1,
            })
              .then(() => {})
              .catch((err) => {
                console.error("Failed to seed default workspaces:", err);
                setWorkspaces(DEFAULT_WORKSPACES);
                seedingRef.current = false;
              });
          }
        } else {
          // workspaces is [] or invalid. Only apply from server — on mobile cold start, a stale
          // cache can have workspaces: [] and would clear custom workspaces until server loads.
          if (fromCache && (!data.workspaces || data.workspaces.length === 0)) {
            // Keep showing defaults until server snapshot; don't overwrite with []
            setWorkspaces((prev) =>
              prev.length > 0 ? prev : DEFAULT_WORKSPACES
            ); 
          } else {
            setWorkspaces(Array.isArray(data.workspaces) ? data.workspaces : []);
          }
        }
        setWorkspacesLoading(false);

        setHasSeenSignupPrompt(data.hasSeenSignupSoftPrompt === true);
        setSignupPromptDismissed(data.signupPromptDismissed === true);
        setSignupBannerDismissed(data.signupBannerDismissed === true);

        const plan = data.subscription?.preferredPlan;
        setPreferredPlan(plan === "monthly" || plan === "annual" ? plan : "annual");

        const layout = data.layoutPreference;
        setLayoutPreferenceState(layout === "compact" || layout === "normal" ? layout : "normal");

        setAccountType(data.accountType ?? null);
        setPlatformFirstSeen(data.platformFirstSeen ?? null);
        setProfileReady(true);
      },
      (error) => {
        console.error("UserProfile onSnapshot error:", error);
        setWorkspacesLoading(false);
        setProfileReady(true);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  const markSignupPromptSeen = useCallback(async () => {
    if (!uid) return;
    try {
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, { hasSeenSignupSoftPrompt: true });
    } catch (err) {
      console.error("Failed to mark signup prompt as seen:", err);
    }
  }, [uid]);

  const dismissSignupPrompt = useCallback(async () => {
    if (!uid) return;
    try {
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, { signupPromptDismissed: true });
    } catch (err) {
      console.error("Failed to dismiss signup prompt:", err);
    }
  }, [uid]);

  const dismissSignupBanner = useCallback(async () => {
    if (!uid) return;
    try {
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, { signupBannerDismissed: true });
    } catch (err) {
      console.error("Failed to dismiss signup banner:", err);
    }
  }, [uid]);

  const setLayoutPreference = useCallback(async (value) => {
    if (!uid || (value !== "normal" && value !== "compact")) return;
    try {
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, { layoutPreference: value });
      setLayoutPreferenceState(value);
    } catch (err) {
      console.error("Failed to save layout preference:", err);
    }
  }, [uid]);

  return (
    <UserProfileContext.Provider
      value={{
        workspaces,
        workspacesLoading,
        hasSeenSignupPrompt,
        signupPromptDismissed,
        signupBannerDismissed,
        markSignupPromptSeen,
        dismissSignupPrompt,
        dismissSignupBanner,
        preferredPlan,
        layoutPreference,
        setLayoutPreference,
        accountType,
        platformFirstSeen,
        profileReady,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => useContext(UserProfileContext);
